import { TossStorageCoordinator, type TossStorageState } from "./toss-storage";
export { APP_DATA_STORAGE_KEY } from "./storage-keys";

let coordinator: TossStorageCoordinator | undefined;
let coordinatorPromise: Promise<TossStorageCoordinator> | undefined;
let fallbackState: TossStorageState = { status: "web" };
const listeners = new Set<() => void>();
const changed = () => { for (const listener of listeners) listener(); };
export const getTossStorageState = () => coordinator?.state ?? fallbackState;
export const subscribeTossStorage = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; };
async function ensureCoordinator() {
  if (coordinator) return coordinator;
  if (!coordinatorPromise) coordinatorPromise = import("@apps-in-toss/web-framework").then(({ Storage }) => {
    coordinator = new TossStorageCoordinator(Storage, localStorage, changed);
    return coordinator;
  }).catch((error) => { coordinatorPromise = undefined; throw error; });
  return coordinatorPromise;
}
export async function retryTossStorage() {
  try { const existing = coordinator; const current = await ensureCoordinator(); if (existing) await current.retry(); else await current.initialize(); }
  catch { fallbackState = { status: "error" }; changed(); }
}
export async function resolveTossStorageConflict(choice: "local" | "toss" | "empty") { return coordinator?.resolve(choice) ?? false; }
export async function deleteAppsInTossData() { if (isAppsInTossHost()) await (await ensureCoordinator()).deleteAll(); }
export async function archiveAppsInTossOriginal(raw: string) { if (isAppsInTossHost()) await (await ensureCoordinator()).archiveBeforeReset(raw); }

export interface AppsInTossSafeAreaInsets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export function appsInTossSafeAreaVariables(insets: AppsInTossSafeAreaInsets) {
  const px = (value: number) => `${Number.isFinite(value) ? Math.max(0, value) : 0}px`;
  return {
    "--toss-safe-top": px(insets.top),
    "--toss-safe-right": px(insets.right),
    "--toss-safe-bottom": px(insets.bottom),
    "--toss-safe-left": px(insets.left),
  } as const;
}

export const shouldHandleAppsInTossBack = (pathname: string) => pathname !== "/";

declare global {
  interface Window {
    ReactNativeWebView?: { postMessage: (message: string) => void };
  }
}

export const isAppsInTossHost = () => typeof window !== "undefined" && Boolean(window.ReactNativeWebView);

export async function prepareAppsInTossHost() {
  if (!isAppsInTossHost()) return;
  try {
    await (await ensureCoordinator()).initialize();
  } catch {
    fallbackState = { status: "error" }; changed();
  }

  const applyInsets = (insets: AppsInTossSafeAreaInsets) => {
    const style = document.documentElement.style;
    for (const [property, value] of Object.entries(appsInTossSafeAreaVariables(insets))) style.setProperty(property, value);
  };
  try {
    const { SafeArea } = await import("@apps-in-toss/web-framework");
    applyInsets(SafeArea.get());
    const unsubscribe = SafeArea.subscribe({ onEvent: applyInsets });
    window.addEventListener("pagehide", unsubscribe, { once: true });
  } catch {
    // standalone CSS safe-area env()가 fallback입니다.
  }
}

export async function mirrorAppsInTossData(serialized: string | undefined) {
  if (!isAppsInTossHost()) return;
  try {
    const existing = coordinator;
    const current = await ensureCoordinator();
    if (!existing) await current.initialize();
    await current.mirror(serialized ?? null);
  } catch {
    fallbackState = { status: "error" }; changed();
  }
}

export async function installAppsInTossBackHandler() {
  if (!isAppsInTossHost()) return () => undefined;
  try {
    const entryPath=window.location.pathname;
    const { graniteEvent, closeView } = await import("@apps-in-toss/web-framework");
    return graniteEvent.addEventListener("backEvent", {
      onEvent: () => {
        if (entryPath.startsWith("/s/") && window.location.pathname===entryPath && !window.history.state?.sheet) { void closeView(); return; }
        if (shouldHandleAppsInTossBack(window.location.pathname)) window.history.back();
      },
    });
  } catch {
    return () => undefined;
  }
}
